// eslint-disable-next-line simple-import-sort/imports
import React, { memo, useCallback, useEffect } from 'react';
// libs
import { Link, useLocation } from 'react-router-dom';
import { Typography } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from 'context/AuthContext';
import routes from 'routes';
import { getDevicesApi } from '../../../../api/getDevicesApi';
// icons
import { ReactComponent as CloseIcon } from 'assets/icons/close.svg';
// icons
import { ReactComponent as LogotypeIcon } from 'assets/icons/logotype.svg';
// styles
import { MenuButton } from '../Header/styled';
import { Divider, Footer, Header, NavLink, NavLinks, Wrapper } from './styled';
import { primary } from 'themes/colors';
// types
import { NavigationMenuProps } from './types';
import { LogoutInputProps } from 'api/types';
import { logoutUserFn } from 'api/authApi';


enum ACCESS_LEVEL {
  Board = 'Board',
  Employee = 'Employee',
}

const NavigationMenu: React.FC<NavigationMenuProps> = ({ handleClose }) => {
  const { user, setUser, isLoggingOut, setIsLoggingOut } = useAuth();
  const location = useLocation();
  const queryClient = useQueryClient();

  const { data } = useQuery(
    ['devices'],
    () => user && getDevicesApi({ cc: user.comcode, tk: 'aeb5d0e6-b574-43c5-b2f5' }),
    {
      enabled: !isLoggingOut && !!user
    }
  );

  const localStorageData = localStorage.getItem('user');
  const userData = localStorageData && JSON.parse(localStorageData);

  const lookLikeAnAccountData = localStorage.getItem('lookLikeAnAccountUser');
  const lookLikeAnAccountUser = lookLikeAnAccountData && JSON.parse(lookLikeAnAccountData);

  const {
    mutate: logoutUser,
  } = useMutation((userData: LogoutInputProps) => logoutUserFn(userData), {
    onSuccess: (data) => {
      if (data) {
        // Cancel all ongoing queries first to prevent any API calls
        queryClient.cancelQueries();
        // Clear queries to remove cached data
        queryClient.clear();
        // Then clear user data
        setUser(null);
        localStorage.removeItem('user');
        localStorage.removeItem('lookLikeAnAccountUser'); // Clear lookLikeAnAccountUser on logout
        document.body.style.overflow = ''; // unlock scrolling after click on logout account inside hamburger menu
        setIsLoggingOut(false); // Reset logout flag
      }
    },
    onError: () => {
      // Even if logout API fails, clear local data
      queryClient.cancelQueries();
      queryClient.clear();
      setUser(null);
      localStorage.removeItem('user');
      localStorage.removeItem('lookLikeAnAccountUser');
      document.body.style.overflow = '';
      setIsLoggingOut(false); // Reset logout flag
    },
    // Error handling removed - now handled globally by the interceptor
  });

  const handleLogOut = useCallback(() => {
    // Set logout flag to prevent any new queries
    setIsLoggingOut(true);

    // First, cancel all ongoing queries to prevent unnecessary API calls
    queryClient.cancelQueries();

    // Check if we have valid token and user data before attempting logout API call
    if (userData?.appUserId && userData?.token) {
      logoutUser({ appUserId: userData.appUserId, token: userData.token });
    } else {
      // If no valid token, just clear local data without API call
      queryClient.clear();
      setUser(null);
      localStorage.removeItem('user');
      localStorage.removeItem('lookLikeAnAccountUser');
      document.body.style.overflow = '';
      setIsLoggingOut(false); // Reset logout flag
    }
  }, [logoutUser, userData?.appUserId, userData?.token, queryClient, setUser, setIsLoggingOut]);

  // Check if user is on Look Like an Account page
  const isOnLookLikeAccountPage = location.pathname === routes.lookLikeAnAccount;

  // Check if Board Member Portal should be shown
  // Board members don't require appUsers - only accessLevel === 'Board' is needed
  // For "Look Like an Account" scenarios, appUsers are still required
  const shouldShowBoardMemberPortal =
    (userData?.accessLevel === ACCESS_LEVEL.Board && !lookLikeAnAccountUser) ||
    (lookLikeAnAccountUser && lookLikeAnAccountUser.accessLevel === ACCESS_LEVEL.Board && lookLikeAnAccountUser.appUsers && lookLikeAnAccountUser.appUsers.length > 0);

  // Debug: Log Board Member Portal visibility check
  useEffect(() => {
    console.log('🔍 DEBUG: Board Member Portal Visibility Check');
    console.log('   userData?.accessLevel:', userData?.accessLevel);
    console.log('   lookLikeAnAccountUser?.accessLevel:', lookLikeAnAccountUser?.accessLevel);
    console.log('   lookLikeAnAccountUser exists:', !!lookLikeAnAccountUser);
    console.log('   Regular Board Check:', userData?.accessLevel === ACCESS_LEVEL.Board && !lookLikeAnAccountUser);
    console.log('   LookLikeAnAccount Board Check:', lookLikeAnAccountUser && lookLikeAnAccountUser.accessLevel === ACCESS_LEVEL.Board && lookLikeAnAccountUser.appUsers && lookLikeAnAccountUser.appUsers.length > 0);
    console.log('   shouldShowBoardMemberPortal:', shouldShowBoardMemberPortal);
    console.log('   isOnLookLikeAccountPage:', isOnLookLikeAccountPage);
  }, [user, userData, lookLikeAnAccountUser, shouldShowBoardMemberPortal, isOnLookLikeAccountPage]);

  // Reminder: Log when Board Member Portal appears in sidebar after login
  useEffect(() => {
    if (shouldShowBoardMemberPortal && !isOnLookLikeAccountPage) {
      console.log('🔔 REMINDER: Board Member Portal is now visible in the Sidebar menu!');
      console.log('   Conditions met:');
      console.log('   - Access Level:', userData?.accessLevel || lookLikeAnAccountUser?.accessLevel);
      if (lookLikeAnAccountUser) {
        console.log('   - Look Like Account mode: AppUsers count:', lookLikeAnAccountUser?.appUsers?.length || 0);
      } else {
        console.log('   - Regular Board member: No appUsers required');
      }
    }
  }, [shouldShowBoardMemberPortal, isOnLookLikeAccountPage, userData?.accessLevel, lookLikeAnAccountUser]);

  return (
    <Wrapper>
      <Header>
        <Link cypress-id='navigationMenu-logotype' to='/'>
          <LogotypeIcon />
        </Link>

        <MenuButton cypress-id='navigationMenu-closeButton' onClick={handleClose}>
          <CloseIcon />
        </MenuButton>
      </Header>

      <NavLinks>
        {!isOnLookLikeAccountPage && (
          <>
            <NavLink cypress-id='navigationMenu-home' to='/'>
              Home
            </NavLink>
            <NavLink cypress-id='navigationMenu-myProperty' to={routes.myProperty.index}>
              My Property
            </NavLink>
            <NavLink cypress-id='navigationMenu-publicDocuments' to={routes.publicDocuments}>
              Public Documents
            </NavLink>
            <NavLink cypress-id='navigationMenu-associationDocuments' to={routes.associationDocuments}>
              Association Documents
            </NavLink>
            <NavLink cypress-id='navigationMenu-communityInformation' to={routes.communityInformation}>
              Community Information
            </NavLink>
            <NavLink cypress-id='navigationMenu-keyPersonnel' to={routes.keyPersonnel}>
              Key Personnel
            </NavLink>

            {data && (
              <NavLink cypress-id='navigationMenu-deviceRequest' to={routes.deviceRequest}>
                Device Request
              </NavLink>
            )}

            <NavLink cypress-id='navigationMenu-allAboutMe' to={routes.allAboutMe}>
              All About Me
            </NavLink>
            <NavLink cypress-id='navigationMenu-ciaServices' to={routes.ciaServices}>
              C.I.A. Services
            </NavLink>
            <NavLink cypress-id='navigationMenu-contactUs' to={routes.contactUs}>
              Contact Us
            </NavLink>

            <Divider />

            {shouldShowBoardMemberPortal && (
              <NavLink cypress-id='navigationMenu-boardMemberPortal' to={routes.boardMemberPortal}>
                Board Member Portal
              </NavLink>
            )}
            {userData?.accessLevel === ACCESS_LEVEL.Employee && (
              <NavLink cypress-id='navigationMenu-lookLikeAccount' to={routes.lookLikeAnAccount}>
                Look Like an Account
              </NavLink>
            )}

            {userData?.accessLevel && Object.keys(ACCESS_LEVEL).includes(userData.accessLevel) && <Divider />}
          </>
        )}

        <NavLink cypress-id='navigationMenu-logOut' to='/' onClick={handleLogOut}>
          Log Out
        </NavLink>
      </NavLinks>

      <Footer>
        <Typography align='center' color={primary[300]}>
          © 2011-2022 C.I.A. Services Inc. All rights reserved.
        </Typography>
      </Footer>
    </Wrapper>
  );
};

export default memo(NavigationMenu);
